import { faker } from "@faker-js/faker";

const STORAGE_KEY = "sbbvibe_player_name";

/**
 * Generates a new three-word player name using faker.js.
 * Example: "fantastic bitcoin protocol"
 * @returns {string} A randomly generated player name.
 */
function generateNewName() {
  // Using hacker theme for potentially amusing/relevant names
  const name = `${faker.hacker.adjective()} ${faker.hacker.noun()} ${faker.hacker.verb()}`;
  // Basic title casing
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Retrieves the player name from localStorage. If not found, generates a new one,
 * stores it, and returns it.
 * @returns {string} The player name.
 */
export function getPlayerName() {
  let name = localStorage.getItem(STORAGE_KEY);
  if (!name) {
    name = generateNewName();
    localStorage.setItem(STORAGE_KEY, name);
  }
  return name;
}

/**
 * Stores the given player name in localStorage.
 * @param {string} name - The player name to store.
 */
export function setPlayerName(name) {
  if (name && typeof name === "string" && name.trim().length > 0) {
    localStorage.setItem(STORAGE_KEY, name.trim());
  } else {
    console.warn("Attempted to set an invalid player name.");
    // Fallback: generate a new name if the provided one is invalid
    const newName = generateNewName();
    localStorage.setItem(STORAGE_KEY, newName);
  }
}

/**
 * Updates the player name only if the provided old name matches the currently stored name.
 * This prevents accidentally overwriting a name if it was changed elsewhere (e.g., another tab)
 * since the game over screen was displayed.
 * @param {string} oldName - The name expected to be currently in storage.
 * @param {string} newName - The new name to set.
 * @returns {boolean} True if the name was updated, false otherwise.
 */
export function updatePlayerNameIfCurrent(oldName, newName) {
  const currentName = localStorage.getItem(STORAGE_KEY);
  if (
    currentName === oldName &&
    newName &&
    typeof newName === "string" &&
    newName.trim().length > 0
  ) {
    localStorage.setItem(STORAGE_KEY, newName.trim());
    return true;
  }
  if (currentName !== oldName) {
    console.warn(
      "Player name was not updated because it changed since the prompt was shown."
    );
  } else {
    console.warn("Attempted to set an invalid new player name during update.");
    // Fallback: Do not update to an invalid name, keep the old one.
  }
  return false;
}
